import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'de', label: 'Deutsch 🇩🇪' },
  { code: 'en', label: 'English 🇬🇧' },
  { code: 'es', label: 'Español 🇪🇸' },
  { code: 'fr', label: 'Français 🇫🇷' },
  { code: 'it', label: 'Italiano 🇮🇹' },
];

const FEATURES = [
  { key: 'factchecking', label: 'Fact-Checking' },
  { key: 'insight-scoring', label: 'Insight Scoring' },
  { key: 'collaborative-growth', label: 'Collaborative Growth' },
];

const ContentManagementTab = () => {
  const [selectedFeature, setSelectedFeature] = useState('factchecking');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [allContents, setAllContents] = useState({});

  useEffect(() => {
    loadContent();
  }, [selectedFeature, selectedLanguage]);

  useEffect(() => {
    loadAllContentsForFeature();
  }, [selectedFeature]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${selectedFeature}/${selectedLanguage}`);
      setContent(response.data.content || '');
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    }
  };

  const loadAllContentsForFeature = async () => {
    try {
      const response = await axios.get(`${API}/content/all/${selectedFeature}`);
      setAllContents(response.data);
    } catch (error) {
      console.error('Error loading all contents:', error);
    }
  };

  const saveContent = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/content`, {
        feature_key: selectedFeature,
        language: selectedLanguage,
        content: content,
      });
      toast.success('Content saved successfully!');
      loadAllContentsForFeature();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Select Feature</label>
          <Select value={selectedFeature} onValueChange={setSelectedFeature}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FEATURES.map((feature) => (
                <SelectItem key={feature.key} value={feature.key}>
                  {feature.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Select Language</label>
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">
          Content (HTML supported)
        </label>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={15}
          placeholder="Enter content here... HTML tags are supported."
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-2">
          You can use HTML tags like &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
        </p>
      </div>

      <Button onClick={saveContent} disabled={loading}>
        {loading ? 'Saving...' : 'Save Content'}
      </Button>

      {/* Status indicator for other languages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Content Status for {FEATURES.find(f => f.key === selectedFeature)?.label}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {LANGUAGES.map((lang) => (
              <div
                key={lang.code}
                className={`p-2 rounded text-center text-xs ${
                  allContents[lang.code] 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {lang.label.split(' ')[1]} {allContents[lang.code] ? '✓' : '✗'}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentManagementTab;
